/*
  eslint-disable camelcase
*/
import { ActionTypes } from './index';
import { DepositKeyInterface } from '../reducers';
import { BeaconDepositStatus } from '../../utils/depositQueueTracker';
import { ValidatorDepositStatus } from '../../utils/validatorStatusChecker';

export enum TransactionStatus {
  'READY',
  'PENDING',
  'STARTED',
  'SUCCEEDED',
  'FAILED',
  'REJECTED',
}

export enum DepositStatus {
  VERIFYING,
  ALREADY_DEPOSITED,
  ALREADY_EXISTS,
  READY_FOR_DEPOSIT,
}

export enum BeaconChainStatus {
  HEALTHY,
  DOWN,
}

export interface UpdateDepositFileNameAction {
  type: ActionTypes.updateDepositFileName;
  payload: string;
}
export interface UpdateDepositFileKeysAction {
  type: ActionTypes.updateDepositFileKeys;
  payload: DepositKeyInterface[];
}
export interface UpdateTransactionStatusAction {
  type: ActionTypes.updateTransactionStatus;
  payload: {
    pubkey: string;
    status: TransactionStatus;
    txHash?: string;
  };
}
export interface UpdateDepositStatusAction {
  type: ActionTypes.updateDepositStatus;
  payload: {
    pubkey: string;
    depositStatus: DepositStatus;
  };
}
export interface UpdateBeaconChainAPIStatusAction {
  type: ActionTypes.updateBeaconChainAPIStatus;
  payload: BeaconChainStatus;
}

export interface BeaconDepositStatusPayload {
  pubkey: string;
  beaconDepositStatus: BeaconDepositStatus;
  depositIndex: number | null;
  beaconProcessingIndex: number | null;
  queuePosition: number | null;
  validatorIndex: number | null;
}

export interface UpdateBeaconDepositStatusAction {
  type: ActionTypes.updateBeaconDepositStatus;
  payload: BeaconDepositStatusPayload;
}

export type DispatchDepositKeysUpdateType = (
  files: DepositKeyInterface[]
) => void;
export type DispatchDepositFileNameUpdateType = (fileName: string) => void;

export const updateDepositFileKeys = (
  files: DepositKeyInterface[]
): UpdateDepositFileKeysAction => {
  return {
    type: ActionTypes.updateDepositFileKeys,
    payload: files,
  };
};

export const updateDepositFileName = (
  fileName: string
): UpdateDepositFileNameAction => {
  return {
    type: ActionTypes.updateDepositFileName,
    payload: fileName,
  };
};

export const updateBeaconChainAPIStatus = (
  status: BeaconChainStatus
): UpdateBeaconChainAPIStatusAction => {
  return {
    type: ActionTypes.updateBeaconChainAPIStatus,
    payload: status,
  };
};

export const updateTransactionStatus = (
  pubkey: string,
  status: TransactionStatus,
  txHash?: string
): UpdateTransactionStatusAction => {
  return {
    type: ActionTypes.updateTransactionStatus,
    payload: {
      pubkey,
      status,
      txHash,
    },
  };
};

export type DispatchTransactionStatusUpdateType = (
  pubkey: string,
  status: TransactionStatus,
  txHash?: string
) => void;

export type DispatchDepositFileNameUpdate = (name: string) => void;

export const updateDepositStatus = (
  pubkey: string,
  depositStatus: DepositStatus
): UpdateDepositStatusAction => {
  return {
    type: ActionTypes.updateDepositStatus,
    payload: {
      pubkey,
      depositStatus,
    },
  };
};

export type DispatchDepositStatusUpdateType = (
  pubkey: string,
  depositStatus: DepositStatus
) => void;

export type DispatchBeaconChainAPIStatusUpdateType = (
  status: BeaconChainStatus
) => void;

export const updateBeaconDepositStatus = (
  payload: BeaconDepositStatusPayload
): UpdateBeaconDepositStatusAction => ({
  type: ActionTypes.updateBeaconDepositStatus,
  payload,
});

export type DispatchBeaconDepositStatusUpdateType = (
  payload: BeaconDepositStatusPayload
) => void;

// Re-export ValidatorDepositStatus for convenience
export { ValidatorDepositStatus };

export interface ValidatorDepositStatusPayload {
  pubkey: string;
  validatorDepositStatus: ValidatorDepositStatus;
  beaconStatus: string | null;
  beaconIndex: number | null;
  beaconBalance: string | null;
  execDeposited: boolean;
  execTxHash: string | null;
  execBlockNumber: number | null;
  execDepositIndex: number | null;
}

export interface UpdateValidatorDepositStatusAction {
  type: ActionTypes.updateValidatorDepositStatus;
  payload: ValidatorDepositStatusPayload;
}

export const updateValidatorDepositStatus = (
  payload: ValidatorDepositStatusPayload
): UpdateValidatorDepositStatusAction => ({
  type: ActionTypes.updateValidatorDepositStatus,
  payload,
});

export type DispatchValidatorDepositStatusUpdateType = (
  payload: ValidatorDepositStatusPayload
) => void;
